import { Button } from '@octano/global-ui';
import { useCallback, useMemo, useRef } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { SectionTitle } from '../../../../components/text';
import { TranslationsKeys } from '../../../../locales/translations';
import { useAcademicOfferFormData } from '../loaders/AcademicOfferFormDataLoader';
import {
  AcademicOfferFinantialConfig,
  AcademicOfferRequirement,
} from '../types';
import { AcademicOfferFormValues } from './AcademicOfferCreateForm';
import { AcademicOfferRequirementsBase } from './AcademicOfferRequirementsBase';
import AcademicOfferRequirementsBaseModal, {
  AcademicOfferRequirementsBaseModalMethods,
} from './AcademicOfferRequirementsBaseModal';
import { AcademicOfferRequirementsFinantial } from './AcademicOfferRequirementsFinantial';

interface Props {
  disabled?: boolean;
}

export function AcademicOfferRequirements({ disabled }: Props) {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );
  const modalRequirementRef = useRef<AcademicOfferRequirementsBaseModalMethods>(
    null,
  );

  const { data: formData } = useAcademicOfferFormData();

  const {
    control,
    formState,
    setValue,
    getValues,
  } = useFormContext<AcademicOfferFormValues>();
  const {
    append: appendRequirement,
    remove: removeRequirement,
    fields: requirements,
  } = useFieldArray({
    control,
    name: 'requirements',
  });
  const {
    append: appendFinantialConfig,
    remove: removeFinantialConfig,
    fields: finantialConfigs,
  } = useFieldArray({
    control,
    name: 'finantials',
  });

  const admissionRequirementsOptions = useMemo(
    () =>
      formData?.admissionRequirements.map((ar) => ({
        value: ar.id,
        label: ar.name,
      })) || [],
    [formData],
  );

  const pendingAdmissionRequirementsOptions = useMemo(() => {
    const selectedIds = requirements.map((r) => r.ceAdmissionRequirementId);
    return admissionRequirementsOptions.filter(
      (option) => !selectedIds.includes(option.value),
    );
  }, [admissionRequirementsOptions, requirements]);

  const onPressCreateRequirement = () => {
    modalRequirementRef.current?.open();
  };

  const handleAddRequirement = useCallback(
    (record: AcademicOfferRequirement) => {
      appendRequirement(record);
    },
    [appendRequirement],
  );

  const handleRemoveRequirement = useCallback(
    (index: number) => {
      removeRequirement(index);
    },
    [removeRequirement],
  );

  const handleAddFinantialConfig = useCallback(
    (record: AcademicOfferFinantialConfig) => {
      appendFinantialConfig(record);
    },
    [appendFinantialConfig],
  );

  const handleRemoveFinantialConfig = useCallback(
    (index: number) => {
      removeFinantialConfig(index);
    },
    [removeFinantialConfig],
  );

  const handleUpdateFinantialConfig = useCallback(
    (index: number, record: AcademicOfferFinantialConfig) => {
      const currentFinantials = getValues('finantials') || [];
      const updatedFinantials = [...currentFinantials];
      updatedFinantials[index].value = record.value;
      setValue('finantials', updatedFinantials);
    },
    [setValue, getValues],
  );

  return (
    <Row>
      <Col xs={12} md={6} className="mb-3">
        <div className="w-100 d-flex justify-content-between align-items-center">
          <div className="flex-fill">
            <SectionTitle text={t('requirements.base.title')} />
            <p>{t('requirements.base.description')}</p>
          </div>
          {!disabled && (
            <div>
              <Button
                text={t('requirements.base.add')}
                icon="plus"
                size="md"
                onClick={onPressCreateRequirement}
              />
            </div>
          )}
        </div>
        <AcademicOfferRequirementsBase
          requirements={requirements}
          admissionRequirements={formData?.admissionRequirements}
          onPressRemoveRequirement={
            disabled ? undefined : handleRemoveRequirement
          }
        />
        <AcademicOfferRequirementsBaseModal
          ref={modalRequirementRef}
          admissionRequirements={pendingAdmissionRequirementsOptions}
          onSave={handleAddRequirement}
        />
      </Col>
      <Col xs={12} md={6} className="mb-3">
        <SectionTitle text={t('requirements.financial.title')} />
        <p>{t('requirements.financial.description')}</p>
        <AcademicOfferRequirementsFinantial
          configs={finantialConfigs}
          currency={formData?.defaultCurrency}
          errors={formState?.errors?.finantials as any}
          onAddConfig={handleAddFinantialConfig}
          onUpdateConfig={handleUpdateFinantialConfig}
          onRemoveConfig={handleRemoveFinantialConfig}
          disabled={disabled}
        />
      </Col>
    </Row>
  );
}
